import { DynamicIcon } from '@/components/icons/DynamicIcon'
import {
  Card,
  // CardAction,
  CardContent,
  CardDescription,
  // CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card'
import { RestSchemas } from '@/contexts/QueryProvider'
import { Link, useSearchParams } from 'react-router'
import { SearchInput } from '../shared/SearchInput'
import { useMemo, useState } from 'react'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { CATALOG_BASE_PATH } from '@/const'
// import WelcomePanelSvg from '@/assets/svg/WelcomePanelSvg'
import { motion, AnimatePresence } from 'motion/react'
import { IconFolderOpenAngledOutline } from '@icons/IconFolderOpenAngledOutline'
import clsx from 'clsx'
import { ReactComponent as MomentumMark } from '@/assets/svg/PwC_momentum-mark_rgb_orange.svg'

type MessagesLandingMenuItem = RestSchemas['Messages.Landing.MenuItem']

function filterItems(items: MessagesLandingMenuItem[], search: string) {
  return items
    .filter((item) => item.page_id.slice(-5) !== '/home')
    .filter((item) => {
      if (search === '') return true

      // Helper function to recursively search through items
      const searchInItem = (item: MessagesLandingMenuItem): boolean => {
        // Check if search term is in current item's name or description
        const searchLower = search.toLowerCase()
        const nameMatch = item.name.toLowerCase().includes(searchLower)
        const descriptionMatch = item.description.toLowerCase().includes(searchLower)

        if (nameMatch || descriptionMatch) {
          return true
        }

        // Recursively check nested items
        return item.items.some((nestedItem) => searchInItem(nestedItem))
      }

      return searchInItem(item)
    })
}

const presenceAnimation = {
  initial: { opacity: 0, scale: 0.8, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  // exit: { opacity: 0, scale: 0.8, y: -20 },
  transition: { duration: 0.2, ease: 'easeOut' as const },
}

export function QuickNavContent({ items }: { items: MessagesLandingMenuItem[] }) {
  const [search, setSearch] = useState('')

  const filteredItems = useMemo(() => filterItems(items, search), [items, search])

  return (
    <ScrollArea className="relative h-full">
      {/* <div className="flex flex-col items-center gap-y-10 px-[8%] pt-10"> */}
      <div className="flex flex-col items-center gap-y-10 px-2">
        {/* <div className="sticky top-0 z-10 flex h-56 w-full items-center overflow-hidden rounded-lg bg-muted/80 px-14 backdrop-blur"> */}
        {/* <div className="sticky top-0 z-10 flex h-56 w-full items-center justify-between overflow-hidden rounded-lg bg-brand-peach px-14 dark:bg-muted/80 dark:bg-none dark:backdrop-blur"> */}
        <div className="sticky top-0 z-10 flex h-56 w-full items-center justify-between overflow-hidden bg-brand-peach px-14 dark:bg-muted/80 dark:bg-none dark:backdrop-blur">
          <div className="flex flex-col gap-y-5">
            <h1 className="text-5xl font-bold text-foreground">Welcome!</h1>
            {/* <p className="text-base text-foreground/60">What do you want to do today?</p> */}
            <p className="text-base font-light text-foreground">What do you want to do today?</p>
            <SearchInput value={search} onChange={setSearch} autoFocus={true} />
          </div>
          <MomentumMark className="size-96" />
        </div>
        <AnimatePresence>
          {filteredItems.length > 0 ? (
            // <div className="grid w-full grid-cols-1 gap-0.5 pt-6 md:grid-cols-2 lg:grid-cols-3">
            // <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-0.5 pt-6 dark:gap-1">
            <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-5 px-[3%] pb-5 pt-6">
              {filteredItems.map((item) => (
                <motion.div
                  layout
                  key={item.name}
                  {...presenceAnimation}
                  // className="overflow-hidden rounded-lg shadow-lg"
                  className="overflow-hidden shadow-lg"
                >
                  <ItemCard item={item} search={search} />
                </motion.div>
              ))}
            </div>
          ) : (
            <NoResults />
          )}
        </AnimatePresence>
      </div>
    </ScrollArea>
  )
}

const NoResults = () => {
  return (
    <motion.div className="flex h-full w-full items-center justify-center pt-20" {...presenceAnimation}>
      <div className="flex flex-col items-center gap-y-10">
        <IconFolderOpenAngledOutline className="size-20 text-muted" />
        <p className="text-lg text-muted-foreground">No results found</p>
      </div>
    </motion.div>
  )
}

const ItemCard = ({ item, search }: { item: MessagesLandingMenuItem; search: string }) => {
  const [searchParams] = useSearchParams()
  const { items: subItems } = item
  const { name, description, page_id, icon_name } = item

  const filteredSubItems = useMemo(() => {
    if (name.toLowerCase().includes(search.toLowerCase()) || description.toLowerCase().includes(search.toLowerCase())) {
      return subItems
    }
    return filterItems(subItems, search)
  }, [subItems, name, description, search])

  return (
    // <Card className="h-full max-h-[250px] border-none bg-offwhite py-0 text-lg">
    // <Card className="h-full max-h-[350px] rounded-none border-none bg-offwhite py-0 text-lg shadow-none">
    <Card className="h-full max-h-[350px] rounded-none border-none bg-background py-0 text-lg shadow-none">
      <ScrollArea className="relative h-full bg-background">
        {/* <CardHeader className="sticky top-0 bg-offwhite/50 py-6 backdrop-blur"> */}
        <CardHeader
          className={clsx(
            'sticky top-0 gap-0 bg-background py-6 backdrop-blur'
            // filteredSubItems.length > 0 ? 'bg-offwhite shadow-sm' : 'border-b-0 bg-tertiary'
          )}
        >
          <div className="flex items-center gap-x-10">
            {/* <DynamicIcon iconName={icon_name ?? ''} className="size-9 text-foreground/60" /> */}
            <DynamicIcon iconName={icon_name ?? ''} className="size-9 text-muted-foreground" />
            <div className="flex flex-col">
              <CardTitle>
                {page_id && !subItems.length ? (
                  <Link
                    to={`${CATALOG_BASE_PATH}${page_id}?${searchParams.toString()}`}
                    className="underline underline-offset-2"
                    // className="text-primary"
                  >
                    {name}
                  </Link>
                ) : (
                  name
                )}
              </CardTitle>
              {/* <CardDescription>{description}</CardDescription> */}
              <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
            </div>
          </div>
          {/* <CardAction>Card Action</CardAction> */}
        </CardHeader>
        {/* <CardContent className="pb-6"> */}
        <CardContent className={filteredSubItems.length > 0 ? 'py-6' : 'py-0'}>
          {/* <ul className="pl-[76px]"> */}

          <ul className="space-y-3">
            {filteredSubItems.map((item) => (
              <MenuItem key={item.page_id} item={item} />
            ))}
          </ul>
        </CardContent>
      </ScrollArea>
    </Card>
  )
}

const MenuItem = ({ item }: { item: MessagesLandingMenuItem }) => {
  const [searchParams] = useSearchParams()
  const listItem = (
    <li className="flex flex-col">
      <span>
        <Link
          className="text-base underline underline-offset-2"
          // className="text-base text-primary"
          to={`${CATALOG_BASE_PATH}${item.page_id}?${searchParams.toString()}`}
        >
          {item.name}
        </Link>
      </span>
      {/* <span className="inline-block text-sm leading-none text-muted-foreground">{item.description}</span> */}
      <span className="inline-block text-sm leading-none text-muted-foreground">{item.description}</span>
    </li>
  )

  if (item.items.length > 0) {
    return (
      <>
        <li className="text-base">{item.name}:</li>
        <ul className="list-inside list-disc pl-4">
          {item.items.map((item) => (
            <MenuItem key={item.page_id ?? '' + item.name} item={item} />
          ))}
        </ul>
      </>
    )
  }

  return listItem
}
